
import { BuildEnvironment } from "@base/BuildEnvironment";
import { ITransformData, TransformData } from "@base/math/TransformData";
import { IVec3 } from "@base/math/Vec3";
import { IdGen } from "@base/util/IdGen";
import { getKeys } from "@base/util/TypeUtil";
import { LogoPrint } from "@biz/definitions/ArticleDefinitions";
import { IModelProductData } from "@biz/definitions/ModelProductDefinitions";
import { IItemData } from "@biz/definitions/ProductDefinitions";
import { ClothType, IMaterialSettings, IModelArticleInfo, MaterialSettingIds } from "@biz/definitions/SunSailDefinitions";
import { IModelProductConfiguration } from "@biz/products/ModelProductDefinitions";

export type IProductTemplateId = "cat" | "drop" | "beanbag" | "chillout" | "stool" | "sojo_sunbed" | "sojo_sunbed_small" | "sojo_sunseat" | "sojo_table" | "pillow_40x40" | "pillow_40x80" | "pillow_75x75" | "reload_table";
export type SurroundingTemplateId = "family" | "dennis" | "mei" | "Tree_01" | "Tree_02" | "Tree_03" | "Tree_04" | "Tree_05" | "Tree_06" | string;
export type ModelTemplateId = IProductTemplateId | SurroundingTemplateId;

export function createModelTemplateMap<T extends Record<string, IModelTemplateData>>(m: T): Record<keyof T, IModelTemplate> {
  const r: Record<keyof T, IModelTemplate> = {} as any;
  for (const k of getKeys(m)) {
    r[k] = {
      id: k as ModelTemplateId,
      ...m[k]
    }
  }
  return r;
}

export interface IModelTemplateData {
  rotationAxis?: { x?: boolean; y?: boolean; z?: boolean; }
  scaleAxis?: { x?: boolean; y?: boolean; z?: boolean; xy?: boolean; xz?: boolean; yz?: boolean; xyz?: boolean; }
  modelInfo?: IModelArticleInfo;
  thumb?: string;
  subCategory: string;
  materialMapping?: Record<string, string | RegExp>;
}

export interface IModelTemplate extends IModelTemplateData {
  id: ModelTemplateId;
}

export interface IModelData extends IItemData {
  itemType: "model";
  templateId: ModelTemplateId;
  transform: ITransformData;
  configuration?: IModelProductConfiguration;
  materials?: Record<string, IMaterialSettings | MaterialSettingIds | undefined>
}

export class ModelData {
  static create(templateId: ModelTemplateId, pos: IVec3): IModelData {
    return {
      itemType: "model",
      id: IdGen.unsecure.get(),
      buildTime: BuildEnvironment.BuildTimeAsNumber,
      version: "2022",
      templateId,
      transform: TransformData.create(pos)
    }
  }

  static isCloth(model: IModelData | IModelProductData, clothType: ClothType): boolean {
    return model.configuration?.cloth?.clothType === clothType;
  }

  static isLogoPossibleForTemplateId(templateId: string): boolean {
    return ["chillout", "beanbag", "stool"].includes(templateId)
  }

  static isLogoPossible(model: IModelData | IModelProductData): boolean {
    const isAustroSail = ModelData.isCloth(model, ClothType.Austrosail_Nano);
    return ModelData.isLogoPossibleForTemplateId(model.templateId) && isAustroSail
  }

  static getLogo(model: IModelData | IModelProductData | undefined): LogoPrint | undefined {
    if (!model)
      return undefined;
    if (!ModelData.isLogoPossible(model))
      return undefined;
    return model.configuration?.logoPrint;
  }
}

